import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Calendar, DollarSign, BarChart3, Activity } from 'lucide-react';
import AppLayout from './components/AppLayout';
import './StockDetail.css';

const LONG_TERM_URL = 'https://tgszfs32y3lqnftikx5jhuika40npdkt.lambda-url.us-east-1.on.aws';

export default function StockDetail({ signOut }) {
  const navigate = useNavigate();
  const location = useLocation();
  const stockData = location.state?.stockData || {};

  const [activeTab, setActiveTab] = useState('overview');
  const [fundamentals, setFundamentals] = useState(null);
  const [loadingFundamentals, setLoadingFundamentals] = useState(false);

  const symbol = stockData.security || 'N/A';

  useEffect(() => {
    if (symbol && symbol !== 'N/A') {
      setLoadingFundamentals(true);
      fetch(`${LONG_TERM_URL}/?securities=${encodeURIComponent(symbol)}`)
        .then(res => res.json())
        .then(data => {
          console.log('StockDetail fundamentals:', data);
          if (Array.isArray(data) && data.length > 0) {
            setFundamentals(data[0]);
          }
        })
        .catch(err => console.log('StockDetail: error fetching fundamentals:', err))
        .finally(() => setLoadingFundamentals(false));
    }
  }, [symbol]);

  // Fresh DB data from LongTerm Lambda takes priority over navigation state
  const f = fundamentals || {};
  const lastSale = f.regularMarketPrice || stockData.lastSale || '0.00';
  const rawMarketCap = f.marketCap ?? stockData.marketCap;
  const marketCap = (rawMarketCap != null && rawMarketCap !== '' && !isNaN(parseFloat(rawMarketCap)) && parseFloat(rawMarketCap) > 0)
    ? parseFloat(rawMarketCap)
    : 'N/A';
  const peRatio = f.trailingPE || stockData.peRatio || 'N/A';
  const dividendYield = f.dividendYield ?? stockData.dividendYield ?? 'N/A';
  const eps = f.epsCurrentYear || stockData.eps || 'N/A';
  const fiftyTwoWeekHigh = f.fiftyTwoWeekHigh || stockData.fiftyTwoWeekHigh || 'N/A';
  const fiftyTwoWeekLow = f.fiftyTwoWeekLow || stockData.fiftyTwoWeekLow || 'N/A';
  const rating = f.averageAnalystRating || stockData.rating || 'N/A';

  // Calculate some derived values
  const isPriceUp = parseFloat(lastSale) > parseFloat(fiftyTwoWeekLow);
  const priceVs52wHighVal = (() => {
    const price = parseFloat(lastSale);
    const high = parseFloat(fiftyTwoWeekHigh);
    if (!isNaN(price) && !isNaN(high) && high > 0) {
      return (price / high * 100).toFixed(2) + '%';
    }
    return 'N/A';
  })();

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'fundamentals', label: 'Fundamentals' },
    { id: 'technicals', label: 'Technicals' },
  ];

  return (
    <AppLayout signOut={signOut}>
      <div className="stock-detail-container">
        {/* Header Section */}
        <div className="stock-detail-header">
          <button onClick={() => navigate(-1)} className="stock-detail-back-button">
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
        </div>

        {/* Stock Title Section */}
        <div className="stock-title-section">
          <div className="stock-symbol-badge">{symbol}</div>
          <div className="stock-price-section">
            <div className="stock-current-price">${parseFloat(lastSale).toFixed(2)}</div>
            <div className={`stock-price-change ${isPriceUp ? 'positive' : 'negative'}`}>
              {isPriceUp ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            </div>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="stock-metrics-grid">
          <div className="metric-card">
            <div className="metric-icon">
              <BarChart3 size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-label">Market Cap</div>
              <div className="metric-value">{loadingFundamentals ? '...' : (marketCap !== 'N/A' ? marketCap.toLocaleString('en-US') : 'N/A')}</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">
              <Activity size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-label">P/E Ratio</div>
              <div className="metric-value">{peRatio}</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">
              <DollarSign size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-label">Dividend Yield</div>
              <div className="metric-value">{dividendYield}%</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">
              <Calendar size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-label">EPS</div>
              <div className="metric-value">{eps}</div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="stock-tabs-container">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`stock-tab ${activeTab === tab.id ? 'active' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="stock-tab-content">
          {activeTab === 'overview' && (
            <div className="overview-section">
              <h3 className="section-title">Company Overview</h3>
              <div className="detail-cards-grid">
                <div className="detail-card">
                  <div className="detail-card-label">Current Price</div>
                  <div className="detail-card-value">${parseFloat(lastSale).toFixed(2)}</div>
                </div>
                <div className="detail-card">
                  <div className="detail-card-label">52-Week Range</div>
                  <div className="detail-card-value">${fiftyTwoWeekLow} - ${fiftyTwoWeekHigh}</div>
                </div>
                <div className="detail-card">
                  <div className="detail-card-label">Analyst Rating</div>
                  <div className="detail-card-value rating-badge">{rating}</div>
                </div>
                <div className="detail-card">
                  <div className="detail-card-label">Price vs 52W High</div>
                  <div className="detail-card-value">{loadingFundamentals ? '...' : priceVs52wHighVal}</div>
                </div>
              </div>

              {stockData.relatedSecurities && (
                <div className="related-securities-section">
                  <h3 className="section-title">Related Securities</h3>
                  <div className="detail-card related-card">
                    <div className="detail-card-value">{stockData.relatedSecurities}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'fundamentals' && (
            <div className="fundamentals-section">
              <h3 className="section-title">Fundamental Analysis</h3>
              <div className="detail-cards-grid">
                <div className="detail-card highlight">
                  <div className="detail-card-label">Market Capitalization</div>
                  <div className="detail-card-value">{marketCap !== 'N/A' ? `$${marketCap.toLocaleString('en-US')}` : 'N/A'}</div>
                </div>
                <div className="detail-card">
                  <div className="detail-card-label">Price-to-Earnings Ratio</div>
                  <div className="detail-card-value">{peRatio}</div>
                  <div className="detail-card-description">P/E Ratio</div>
                </div>
                <div className="detail-card">
                  <div className="detail-card-label">Earnings Per Share</div>
                  <div className="detail-card-value">${eps}</div>
                  <div className="detail-card-description">EPS (TTM)</div>
                </div>
                <div className="detail-card">
                  <div className="detail-card-label">Dividend Yield</div>
                  <div className="detail-card-value">{dividendYield}%</div>
                  <div className="detail-card-description">Annual Dividend</div>
                </div>
                <div className="detail-card">
                  <div className="detail-card-label">52-Week High</div>
                  <div className="detail-card-value">${fiftyTwoWeekHigh}</div>
                </div>
                <div className="detail-card">
                  <div className="detail-card-label">52-Week Low</div>
                  <div className="detail-card-value">${fiftyTwoWeekLow}</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'technicals' && (
            <div className="technicals-section">
              <h3 className="section-title">Technical Indicators</h3>
              <div className="detail-cards-grid">
                {stockData.expiration && (
                  <div className="detail-card">
                    <div className="detail-card-label">Expiration Date</div>
                    <div className="detail-card-value">{stockData.expiration}</div>
                  </div>
                )}
                {stockData.strike && (
                  <div className="detail-card">
                    <div className="detail-card-label">Strike Price</div>
                    <div className="detail-card-value">${stockData.strike}</div>
                  </div>
                )}
                {stockData.profit && (
                  <div className="detail-card">
                    <div className="detail-card-label">Profit Percentage</div>
                    <div className="detail-card-value">{stockData.profit}%</div>
                  </div>
                )}
                {stockData.depth && (
                  <div className="detail-card">
                    <div className="detail-card-label">Depth Percentage</div>
                    <div className="detail-card-value">{stockData.depth}%</div>
                  </div>
                )}
                {stockData.wAvgChange && (
                  <div className="detail-card">
                    <div className="detail-card-label">Weighted Avg Change</div>
                    <div className="detail-card-value">{stockData.wAvgChange}%</div>
                  </div>
                )}
                {stockData.earningsDate && (
                  <div className="detail-card">
                    <div className="detail-card-label">Earnings Date</div>
                    <div className="detail-card-value">{stockData.earningsDate}</div>
                  </div>
                )}
                {stockData.ranking && (
                  <div className="detail-card">
                    <div className="detail-card-label">Ranking</div>
                    <div className="detail-card-value">{stockData.ranking}</div>
                  </div>
                )}
                {stockData.analystRating && (
                  <div className="detail-card">
                    <div className="detail-card-label">Analyst Rating</div>
                    <div className="detail-card-value">{stockData.analystRating}</div>
                  </div>
                )}
                {stockData.bid && (
                  <div className="detail-card">
                    <div className="detail-card-label">Option Bid</div>
                    <div className="detail-card-value">${stockData.bid}</div>
                  </div>
                )}
                {stockData.offer && (
                  <div className="detail-card">
                    <div className="detail-card-label">Option Offer</div>
                    <div className="detail-card-value">${stockData.offer}</div>
                  </div>
                )}
                {stockData.openInterest && (
                  <div className="detail-card">
                    <div className="detail-card-label">Open Interest</div>
                    <div className="detail-card-value">{stockData.openInterest}</div>
                  </div>
                )}
                {stockData.volume && (
                  <div className="detail-card">
                    <div className="detail-card-label">Volume</div>
                    <div className="detail-card-value">{stockData.volume.toLocaleString()}</div>
                  </div>
                )}
                {stockData.impliedVol && (
                  <div className="detail-card">
                    <div className="detail-card-label">Implied Volatility</div>
                    <div className="detail-card-value">{stockData.impliedVol}%</div>
                  </div>
                )}
                {stockData.impliedPeriod && (
                  <div className="detail-card">
                    <div className="detail-card-label">Implied Vol Period</div>
                    <div className="detail-card-value">{stockData.impliedPeriod}</div>
                  </div>
                )}
                {stockData.minPrice && (
                  <div className="detail-card">
                    <div className="detail-card-label">Min Price (34%)</div>
                    <div className="detail-card-value">${stockData.minPrice}</div>
                  </div>
                )}
                {stockData.maxPrice && (
                  <div className="detail-card">
                    <div className="detail-card-label">Max Price (34%)</div>
                    <div className="detail-card-value">${stockData.maxPrice}</div>
                  </div>
                )}
                {stockData.stockOffer && (
                  <div className="detail-card">
                    <div className="detail-card-label">Stock Offer Price</div>
                    <div className="detail-card-value">${stockData.stockOffer}</div>
                  </div>
                )}
                {stockData.annualProfit && (
                  <div className="detail-card">
                    <div className="detail-card-label">Annual Profit</div>
                    <div className="detail-card-value">{stockData.annualProfit}%</div>
                  </div>
                )}
                {stockData.maxChange && (
                  <div className="detail-card">
                    <div className="detail-card-label">Max % Change</div>
                    <div className="detail-card-value">{stockData.maxChange}%</div>
                  </div>
                )}
                {stockData.dbTimestamp && (
                  <div className="detail-card">
                    <div className="detail-card-label">Last Updated</div>
                    <div className="detail-card-value">{stockData.dbTimestamp}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
